import { useCallback, useEffect, useState } from "react";
import { epsonRtApi, type EpsonRtProbe } from "../../api/epsonRtApi";
import { useAuth } from "../../auth/AuthContext";
import { canAccessPosMonitor } from "../../auth/roles";

const STORAGE_KEY = "corevian.epsonRt";

function loadSaved() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { host: "", port: 80 };
    const parsed = JSON.parse(raw) as { host?: string; port?: number };
    return { host: parsed.host || "", port: Number(parsed.port) || 80 };
  } catch {
    return { host: "", port: 80 };
  }
}

function severityBadge(severity: string) {
  if (severity === "HIGH") return "text-bg-danger";
  if (severity === "MEDIUM") return "text-bg-warning";
  return "text-bg-secondary";
}

function Lamp({ on, color, label }: { on: boolean; color: "green" | "red"; label: string }) {
  const background = on ? (color === "green" ? "#198754" : "#dc3545") : "#dee2e6";
  return (
    <div className="d-flex align-items-center gap-2">
      <span
        aria-hidden
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background,
          boxShadow: on ? `0 0 12px ${background}` : "none",
          border: "2px solid rgba(0,0,0,0.15)",
        }}
      />
      <span className="fw-semibold">{label}</span>
    </div>
  );
}

export default function EpsonRtPage() {
  const { token, user } = useAuth();
  const canView = canAccessPosMonitor(user?.role);
  const saved = loadSaved();
  const [host, setHost] = useState(saved.host);
  const [port, setPort] = useState(saved.port);
  const [probe, setProbe] = useState<EpsonRtProbe | null>(null);
  const [loading, setLoading] = useState(false);
  const [correcting, setCorrecting] = useState("");
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const readStatus = useCallback(async () => {
    if (!token || !canView) return;
    const ip = host.trim();
    if (!ip) {
      setError("Inserisci l’indirizzo IP del registratore.");
      return;
    }
    setLoading(true);
    setError("");
    setNote("");
    try {
      const res = await epsonRtApi.status(token, ip, port);
      setProbe(res);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ host: ip, port }));
    } catch (e: any) {
      const msg = e?.message || "Lettura registratore fallita";
      setError(msg.includes("403") ? "Accesso riservato a Operatore, Manager o Admin." : msg);
    } finally {
      setLoading(false);
    }
  }, [token, canView, host, port]);

  useEffect(() => {
    if (!autoRefresh || !canView) return;
    const timer = window.setInterval(() => {
      void readStatus();
    }, 15000);
    return () => window.clearInterval(timer);
  }, [autoRefresh, canView, readStatus]);

  async function correct(code: string) {
    if (!token) return;
    setCorrecting(code);
    setError("");
    setNote("");
    try {
      const res = await epsonRtApi.recover(token, host.trim(), port, code);
      setProbe(res.probe);
      setNote(res.note);
    } catch (e: any) {
      setError(e?.message || "Correzione non riuscita");
    } finally {
      setCorrecting("");
    }
  }

  if (!canView) {
    return (
      <div className="container-fluid py-3">
        <div className="alert alert-warning mb-0">Accesso riservato a Operatore, Manager o Admin.</div>
      </div>
    );
  }

  const connected = probe?.connected === true;

  return (
    <div className="container-fluid py-3">
      <h4 className="mb-1">Registratore di cassa telematico Epson</h4>
      <p className="text-muted">
        La comunicazione IP con la stampante la apre NATPOS sul PC cassa del punto vendita, non questo server. Qui restano indirizzo e lettura solo se Corevian è nella stessa rete del negozio.
      </p>

      <div className="card shadow-sm mb-3">
        <div className="card-body d-flex flex-wrap gap-4 align-items-center">
          <Lamp on={Boolean(probe) && connected} color="green" label="Verde — collegata" />
          <Lamp on={Boolean(probe) && !connected} color="red" label="Rosso — non collegata" />
          <span className="text-muted small">{probe ? probe.message : "In attesa di lettura"}</span>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <h6 className="card-title">Indirizzo IP di comunicazione</h6>
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label small">IP registratore</label>
              <input className="form-control" value={host} onChange={(e) => setHost(e.target.value)} placeholder="192.168.1.50" />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Porta</label>
              <input
                type="number"
                className="form-control"
                value={port}
                min={1}
                max={65535}
                onChange={(e) => setPort(Number(e.target.value) || 80)}
              />
            </div>
            <div className="col-md-auto d-flex gap-2">
              <button className="btn btn-primary" type="button" onClick={() => void readStatus()} disabled={loading}>
                {loading ? "Lettura..." : "Leggi stato"}
              </button>
            </div>
            <div className="col-md-auto form-check mb-2">
              <input
                id="epsonAuto"
                className="form-check-input"
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="epsonAuto">Aggiorna ogni 15 secondi</label>
            </div>
          </div>
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {note ? <div className="alert alert-info">{note}</div> : null}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <h6 className="card-title">Driver stampante</h6>
          {probe ? (
            <>
              <div><strong>{probe.driver.name}</strong> — {probe.driver.protocol}</div>
              <div className="small text-muted">{probe.driver.description}</div>
              <div className="small mt-2">
                Endpoint: http://{probe.host}:{probe.port}{probe.driver.path}
                {probe.latencyMs != null ? ` · ${probe.latencyMs} ms` : ""}
              </div>
              {probe.fields.cpuRel || probe.fields.mfRel ? (
                <div className="small">Firmware CPU {probe.fields.cpuRel || "-"} · memoria fiscale {probe.fields.mfRel || "-"}</div>
              ) : null}
            </>
          ) : (
            <div className="text-muted">Driver Epson FpMate XML, porta 80, percorso /cgi-bin/fpmate.cgi. Premi Leggi stato per il collegamento.</div>
          )}
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <h6 className="card-title">Stampante telematica fiscale — problematiche</h6>
          {!probe ? (
            <div className="text-muted">Nessuna lettura ancora.</div>
          ) : probe.problems.length === 0 ? (
            <div className="text-success">Nessuna problematica letta dal registratore.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Gravità</th>
                    <th>Problema</th>
                    <th>Correzione</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {probe.problems.map((row) => (
                    <tr key={`${row.code}-${row.detail}`}>
                      <td><span className={`badge ${severityBadge(row.severity)}`}>{row.severity}</span></td>
                      <td>
                        <div className="fw-semibold">{row.title}</div>
                        <div className="small text-muted">{row.detail}</div>
                      </td>
                      <td className="small">{row.action}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          disabled={correcting === row.code || loading}
                          onClick={() => void correct(row.code)}
                        >
                          {correcting === row.code ? "Verifica..." : "Correggi"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
